"use client";

/**
 * `<SimulateHub>` — the signed-in feature hub for `/simulate`.
 *
 * This is the client component that previously lived inline in
 * `src/app/simulate/page.tsx`, moved here when the route was restructured
 * into the Compass-style auth split (anonymous visitors get
 * `<SimulateProductPage>`; authenticated users get this hub).
 *
 * RULE: no marketing animation on logged-in/app pages. The Boardroom hero
 * (`<SimulateHero>`), the `<ExperimentalNotice>` band, and the Galton
 * consensus board (`<ScenarioGalton>`) live ONLY on the anonymous
 * marketing page (`simulate-product-page.tsx`). This hub opens with a
 * plain text header + CTAs instead.
 *
 * Everything functional stays intact: credit balance + free-Pro-run state
 * (`/api/simulate/credits`), Stripe credit-pack checkout
 * (`/api/simulate/credits/purchase` + the `?credits=added|cancelled`
 * return-toast logic), the tier grid, simulation history
 * (`/api/simulate/history`), the "Start Simulation" gating into
 * `/simulate/setup`, and the bottom `SIMULATION_DISCLAIMER`. The run flow
 * itself remains auth-gated downstream: `/simulate/setup` redirects
 * unauthenticated visitors to /login with a callback, and the credits /
 * purchase APIs are session-protected.
 */

import { Suspense, useEffect, useState } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useSession } from "next-auth/react";
import { Navbar } from "@/components/layout/navbar";
import { Footer } from "@/components/layout/footer";
import { Button } from "@/components/ui/button";
import {
  Zap,
  Gauge,
  Telescope,
  Sparkles,
  Clock,
  TrendingUp,
  Check,
  X,
} from "lucide-react";
import { SIMULATION_TIERS, SIMULATION_DISCLAIMER } from "@/lib/types/simulation";

type PackKey = "3" | "10" | "25";
const CREDIT_PACKS: Array<{
  key: PackKey;
  credits: number;
  price: number;
  badge?: string;
  highlight?: boolean;
  perCredit: number;
}> = [
  { key: "3", credits: 3, price: 12, perCredit: 4.0 },
  { key: "10", credits: 10, price: 35, badge: "Most Popular", highlight: true, perCredit: 3.5 },
  { key: "25", credits: 25, price: 75, badge: "Best Value", perCredit: 3.0 },
];

interface CreditState {
  creditsRemaining: number;
  isPro: boolean;
  freeRunAvailable: boolean;
  freeMonthlyUsed: boolean;
  freeMonthlyResetAt: string | null;
}

interface SimHistoryItem {
  id: string;
  slug: string;
  stockName: string;
  stockTicker: string;
  status: string;
  tier: string;
  consensusDirection: string | null;
  consensusPercentage: number | null;
  createdAt: string;
}

export function SimulateHub() {
  return (
    <Suspense>
      <SimulateHubInner />
    </Suspense>
  );
}

function SimulateHubInner() {
  const { status } = useSession();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [credits, setCredits] = useState<CreditState | null>(null);
  const [history, setHistory] = useState<SimHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState<PackKey | null>(null);
  const [toast, setToast] = useState<{ kind: "success" | "error"; msg: string } | null>(null);

  // Stripe checkout return — show success/cancel toast
  useEffect(() => {
    const flag = searchParams.get("credits");
    if (flag === "added") {
      setToast({
        kind: "success",
        msg: "Payment successful. Credits will appear in a few seconds.",
      });
      // Strip the query param without navigation
      const url = new URL(window.location.href);
      url.searchParams.delete("credits");
      window.history.replaceState({}, "", url.toString());
      // Refresh credit balance a couple of times in case webhook is delayed
      const refresh = async () => {
        try {
          const r = await fetch("/api/simulate/credits");
          if (r.ok) setCredits(await r.json());
        } catch {
          /* ignore */
        }
      };
      refresh();
      const t = setTimeout(refresh, 3000);
      const t2 = setTimeout(refresh, 8000);
      return () => {
        clearTimeout(t);
        clearTimeout(t2);
      };
    }
    if (flag === "cancelled") {
      setToast({ kind: "error", msg: "Purchase cancelled. No charge was made." });
      const url = new URL(window.location.href);
      url.searchParams.delete("credits");
      window.history.replaceState({}, "", url.toString());
    }
  }, [searchParams]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 6000);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    if (status !== "authenticated") return;
    (async () => {
      try {
        const [creditsRes, histRes] = await Promise.all([
          fetch("/api/simulate/credits"),
          fetch("/api/simulate/history").catch(() => null),
        ]);
        if (creditsRes.ok) setCredits(await creditsRes.json());
        if (histRes?.ok) {
          const data = await histRes.json();
          setHistory(data.simulations ?? []);
        }
      } catch {
        /* ignore */
      } finally {
        setLoading(false);
      }
    })();
  }, [status]);

  const isAuthed = status === "authenticated";
  const canRun =
    credits && (credits.freeRunAvailable || credits.creditsRemaining > 0);

  async function handlePurchase(pack: PackKey) {
    // Defensive: if the client session has expired since the server
    // rendered this hub, send to login first (the purchase API is
    // session-protected), then return to the credit packs.
    if (!isAuthed) {
      router.push(`/login?callbackUrl=${encodeURIComponent("/simulate")}`);
      return;
    }
    setPurchasing(pack);
    try {
      const res = await fetch("/api/simulate/credits/purchase", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pack }),
      });
      const data = await res.json();
      if (data.success && data.url) {
        window.location.href = data.url;
        return;
      }
      setToast({
        kind: "error",
        msg: data.error ?? "Could not start checkout. Please try again.",
      });
    } catch {
      setToast({ kind: "error", msg: "Could not start checkout. Please try again." });
    } finally {
      setPurchasing(null);
    }
  }

  return (
    <div className="min-h-screen bg-navy text-foreground">
      <Navbar />

      {/* Toast */}
      {toast && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 max-w-md w-[92%]">
          <div
            className={`flex items-start gap-2 px-4 py-3 rounded-lg border shadow-lg text-sm ${
              toast.kind === "success"
                ? "bg-cgreen/10 border-cgreen/40 text-cgreen"
                : "bg-cred/10 border-cred/40 text-cred"
            }`}
          >
            {toast.kind === "success" ? (
              <Check size={16} className="mt-0.5 shrink-0" />
            ) : (
              <X size={16} className="mt-0.5 shrink-0" />
            )}
            <div className="flex-1">{toast.msg}</div>
            <button
              onClick={() => setToast(null)}
              className="text-current opacity-60 hover:opacity-100"
              aria-label="Dismiss"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      <main className="max-w-5xl mx-auto px-4 md:px-6 py-8 md:py-12">
        {/* Plain text header — no animation on logged-in pages. The CTA
            gating mirrors the credit-card buttons: still-resolving sessions
            go to /simulate/setup, which itself redirects unauthenticated
            visitors to /login with a callback. */}
        <header className="mb-8">
          <h1 className="font-serif text-2xl md:text-3xl font-bold text-foreground mb-2">
            Simulate
          </h1>
          <p className="text-sm md:text-base text-muted-foreground max-w-2xl">
            Run a panel of 20 to 80 AI investor personas that debate any stock
            across structured rounds. Experimental AI simulation, not
            investment advice.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-5">
            <Button asChild size="lg">
              <Link href={!isAuthed || canRun ? "/simulate/setup" : "#buy-credits"}>
                {isAuthed && !canRun ? "Get credits to start" : "Start a simulation"}
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <a href="#buy-credits">Buy credits</a>
            </Button>
          </div>
        </header>

        {/* Credit card — signed-in account state */}
        {isAuthed && (
          <div className="bg-gradient-to-br from-surface to-surface/50 border border-surface-light/30 rounded-xl p-5 md:p-6 mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                Your Simulation Credits
              </div>
              <div className="text-3xl font-bold text-foreground">
                {loading ? "…" : credits?.creditsRemaining ?? 0}
                <span className="text-sm text-muted-foreground font-normal ml-2">
                  credit{credits?.creditsRemaining === 1 ? "" : "s"} remaining
                </span>
              </div>
              {credits?.freeRunAvailable && (
                <div className="mt-1 text-xs text-cgreen flex items-center gap-1">
                  <Sparkles size={12} />
                  Free Pro run available this month
                </div>
              )}
              {credits?.isPro && credits.freeMonthlyUsed && credits.freeMonthlyResetAt && (
                <div className="mt-1 text-xs text-muted-foreground">
                  Free monthly run resets{" "}
                  {new Date(credits.freeMonthlyResetAt).toLocaleDateString()}
                </div>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button asChild variant="outline" size="sm">
                <a href="#buy-credits">Buy credits</a>
              </Button>
              <Button asChild disabled={!canRun} size="sm">
                <Link href={canRun ? "/simulate/setup" : "#buy-credits"}>
                  {canRun ? "Start Simulation" : "No credits"}
                </Link>
              </Button>
            </div>
          </div>
        )}

        {/* Credit pack purchase grid */}
        <section id="buy-credits" className="mb-10 scroll-mt-24">
          <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
            <div>
              <h2 className="font-serif text-lg font-semibold">Buy simulation credits</h2>
              <p className="text-xs text-muted-foreground">
                One credit runs one simulation. Credits never expire.
                {credits?.isPro && " Pro subscribers also get 1 free simulation every month."}
                {!isAuthed && " Sign in to purchase and run simulations."}
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {CREDIT_PACKS.map((p) => {
              const isHighlight = p.highlight;
              return (
                <div
                  key={p.key}
                  className={`relative bg-surface rounded-2xl p-6 transition ${
                    isHighlight
                      ? "border-2 border-blue shadow-[0_0_40px_rgba(59,130,246,0.15)]"
                      : "border border-surface-light/30 hover:border-blue/40"
                  }`}
                >
                  {p.badge && (
                    <div
                      className={`absolute -top-3 left-1/2 -translate-x-1/2 text-[11px] font-semibold px-3 py-1 rounded-full ${
                        isHighlight
                          ? "bg-blue text-white"
                          : "bg-cgreen/20 text-cgreen border border-cgreen/40"
                      }`}
                    >
                      {p.badge}
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                    {p.credits} Credit{p.credits === 1 ? "" : "s"}
                  </div>
                  <div className="flex items-baseline gap-1 mb-1">
                    <div className="text-3xl font-bold">${p.price}</div>
                  </div>
                  <div className="text-[11px] text-muted-foreground mb-4">
                    ${p.perCredit.toFixed(2)} per simulation
                  </div>
                  <Button
                    onClick={() => handlePurchase(p.key)}
                    disabled={purchasing !== null}
                    className="w-full"
                    size="sm"
                    variant={isHighlight ? "default" : "outline"}
                  >
                    {purchasing === p.key ? "Loading…" : `Buy ${p.credits} credits`}
                  </Button>
                </div>
              );
            })}
          </div>
        </section>

        {/* Tier overview */}
        <h2 className="font-serif text-lg font-semibold mb-3">Choose your tier</h2>
        <div className="grid md:grid-cols-3 gap-4 mb-10">
          {(["quick_scan", "standard", "deep_dive"] as const).map((tier) => {
            const meta = SIMULATION_TIERS[tier];
            const Icon = tier === "quick_scan" ? Zap : tier === "standard" ? Gauge : Telescope;
            return (
              <div
                key={tier}
                className="bg-surface border border-surface-light/30 rounded-xl p-5 hover:border-blue/40 transition"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="text-blue" size={18} />
                  <div className="font-semibold">{meta.label}</div>
                </div>
                <div className="text-xs text-muted-foreground mb-3">{meta.description}</div>
                <div className="text-[11px] text-muted-foreground space-y-1">
                  <div>
                    <strong className="text-foreground">{meta.agents}</strong> investor personas
                  </div>
                  <div>
                    <strong className="text-foreground">{meta.rounds}</strong> debate rounds
                  </div>
                  <div>{meta.estimatedTime}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* How it works */}
        <h2 className="font-serif text-lg font-semibold mb-3">How it works</h2>
        <div className="grid md:grid-cols-5 gap-3 mb-10">
          {[
            { step: 1, title: "Seed", desc: "Gather financials, news, and your thesis" },
            { step: 2, title: "Graph", desc: "Map entities, risks, and catalysts" },
            { step: 3, title: "Panel", desc: "Spin up 20-80 investor personas" },
            { step: 4, title: "Debate", desc: "Multi-round discussion with position changes" },
            { step: 5, title: "Report", desc: "Synthesize consensus, dissents, scenarios" },
          ].map((s) => (
            <div
              key={s.step}
              className="bg-surface/60 border border-surface-light/20 rounded-lg p-3"
            >
              <div className="text-[10px] text-blue font-bold tracking-wide mb-1">
                STAGE {s.step}
              </div>
              <div className="text-sm font-semibold">{s.title}</div>
              <div className="text-[11px] text-muted-foreground">{s.desc}</div>
            </div>
          ))}
        </div>

        {/* History */}
        {history.length > 0 && (
          <>
            <h2 className="font-serif text-lg font-semibold mb-3 flex items-center gap-2">
              <Clock size={16} />
              Your recent simulations
            </h2>
            <div className="bg-surface border border-surface-light/30 rounded-xl divide-y divide-surface-light/20 mb-10">
              {history.slice(0, 8).map((h) => (
                <Link
                  key={h.id}
                  href={
                    h.status === "completed"
                      ? `/simulate/${h.slug}`
                      : `/simulate/live/${h.id}`
                  }
                  className="flex items-center justify-between px-4 py-3 hover:bg-navy-mid transition no-underline"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">
                      {h.stockName}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      {h.stockTicker} · {SIMULATION_TIERS[h.tier as keyof typeof SIMULATION_TIERS]?.label ?? h.tier}
                      {" · "}
                      {new Date(h.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    {h.status === "completed" && h.consensusDirection ? (
                      <span
                        className={`px-2 py-0.5 rounded-full font-semibold ${
                          h.consensusDirection === "strong_buy" ||
                          h.consensusDirection === "buy"
                            ? "bg-cgreen/20 text-cgreen"
                            : h.consensusDirection === "strong_sell" ||
                                h.consensusDirection === "sell"
                              ? "bg-cred/20 text-cred"
                              : "bg-blue/20 text-blue"
                        }`}
                      >
                        <TrendingUp size={10} className="inline mr-1" />
                        {h.consensusDirection.replace("_", " ")}
                        {h.consensusPercentage
                          ? ` ${Math.round(h.consensusPercentage)}%`
                          : ""}
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full bg-surface-light/30 text-muted-foreground">
                        {h.status}
                      </span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}

        <div className="text-[11px] text-muted-foreground border border-surface-light/20 rounded-lg p-3 leading-relaxed">
          {SIMULATION_DISCLAIMER}
        </div>
      </main>

      <Footer />
    </div>
  );
}
